/* eslint-disable @typescript-eslint/no-explicit-any */
import { SupplierType, type RefundItem } from '@/data/subsidy/interfaces';
import { paymentTypes } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/constants';
import {
  PaymentReceiptsTypes,
  PixKeyType,
  TypeOfBankTransfer,
} from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/interfaces';
import type { InvoiceFormSchema } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/schema';

const isoToBrDate = (iso: string) => {
  const date = new Date(iso);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const base64ToBlob = (
  base64Data: string,
  contentType = '',
  sliceSize = 512
) => {
  if (!base64Data) return null;

  const base64String = base64Data?.includes(',')
    ? base64Data?.split(',')[1]
    : base64Data;

  const cleanBase64 = base64String?.replace(/\s/g, '');

  const byteCharacters = atob(cleanBase64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters?.length; offset += sliceSize) {
    const slice = byteCharacters?.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice?.length; i++) {
      byteNumbers[i] = slice?.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: contentType });
};

const resourceFiles = (
  files: {
    content: {
      filename: string;
      blob: string | Blob;
      content_type: string;
    };
  }[]
) => {
  return files
    ?.map((file) => {
      const { blob, content_type, filename } = file.content;

      if (blob instanceof Blob) {
        return new File([blob], filename, {
          type: content_type,
        });
      }

      const blobFile = base64ToBlob(blob, content_type);

      if (!blobFile) return null;

      return new File([blobFile], filename, {
        type: content_type,
      });
    })
    .filter((file): file is File => file !== null);
};

export const refundRequestToForm = (
  refundRequest: RefundItem
): InvoiceFormSchema => {
  const isPix = Boolean(
    refundRequest?.banking_info.pix_key && refundRequest?.banking_info.pix_type
  );

  const receipts = refundRequest?.receipts ?? [];
  const purchaseReceipts = receipts.find(
    (r) => !paymentTypes.includes(r.type as PaymentReceiptsTypes)
  );
  const paymentReceipts = receipts.find(
    (r) =>
      r.type === PaymentReceiptsTypes.Payment ||
      r.type === PaymentReceiptsTypes.BillingStatement
  );
  const othersReceipts = receipts.find(
    (r) => r.type === PaymentReceiptsTypes.Other
  );

  return {
    supplier_name: refundRequest?.supplier_name,
    supplier_document: refundRequest?.supplier_document,
    supplier_type: refundRequest?.international_supplier
      ? SupplierType.InternationalSupplier
      : refundRequest?.supplier_type,

    receiver_fiscal_document: refundRequest?.receiver_fiscal_document,
    purchase_receipt_type: purchaseReceipts?.type as any,
    purchase_date: isoToBrDate(refundRequest?.purchase_date),
    purchase_amount: Number(refundRequest?.purchase_amount),
    purchase_attachments: resourceFiles(
      (purchaseReceipts?.attachments as any) || []
    ),

    payment_receipt_type: paymentReceipts?.type as any,
    payment_date: isoToBrDate(refundRequest?.payment_date),
    payment_amount: Number(refundRequest?.payment_amount),
    payment_attachments: resourceFiles(
      (paymentReceipts?.attachments as any) || []
    ),

    reference_month: refundRequest?.reference_month
      ? refundRequest.reference_month
      : undefined,

    optional_files: resourceFiles((othersReceipts?.attachments as any) || []),

    bank_transfer_type: isPix ? TypeOfBankTransfer.Pix : TypeOfBankTransfer.Ted,
    holder_name: refundRequest?.banking_info?.holder_name,

    bank: refundRequest?.banking_info?.bank,
    agency: refundRequest?.banking_info?.agency,
    account_number: refundRequest?.banking_info?.account_number,

    pix_key: refundRequest?.banking_info?.pix_key,
    pix_key_type: refundRequest?.banking_info?.pix_type as PixKeyType,
  };
};
